import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/components/ui/use-toast';

type Role = 'family' | 'caregiver';

export function RoleSelectPage() {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { accessToken, setUser } = useAuthStore();
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!selectedRole) {
      toast({
        title: t('common.error'),
        description: t('auth.selectRole'),
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/v1/auth/select-role', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ role: selectedRole }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t('auth.roleSetFailed'));
      }

      setUser(data.data.user);

      toast({
        title: t('common.success'),
        description: t('auth.accountSetup'),
        variant: 'success',
      });

      const redirectPath = selectedRole === 'caregiver' ? '/caregiver' : '/family';
      navigate(redirectPath);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('common.somethingWrong'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const roles = [
    {
      id: 'family' as Role,
      title: t('auth.roleFamily'),
      description: t('auth.roleFamilyDesc'),
      icon: <FamilyRestroomIcon sx={{ fontSize: 48 }} />,
      features: [
        t('auth.roleFamilyFeature1'),
        t('auth.roleFamilyFeature2'),
        t('auth.roleFamilyFeature3'),
      ],
    },
    {
      id: 'caregiver' as Role,
      title: t('auth.roleCaregiver'),
      description: t('auth.roleCaregiverDesc'),
      icon: <VolunteerActivismIcon sx={{ fontSize: 48 }} />,
      features: [
        t('auth.roleCaregiverFeature1'),
        t('auth.roleCaregiverFeature2'),
        t('auth.roleCaregiverFeature3'),
      ],
    },
  ];

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          {t('auth.selectRole')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('auth.selectRoleDesc')}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        {roles.map((role) => (
          <Card
            key={role.id}
            variant="outlined"
            sx={{
              borderWidth: 2,
              borderColor: selectedRole === role.id ? 'primary.main' : 'divider',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.light',
              },
            }}
          >
            <CardActionArea onClick={() => setSelectedRole(role.id)}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      color: selectedRole === role.id ? 'primary.main' : 'text.secondary',
                    }}
                  >
                    {role.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {role.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {role.description}
                    </Typography>
                    <List dense disablePadding>
                      {role.features.map((feature) => (
                        <ListItem key={feature} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <CheckCircleIcon
                              fontSize="small"
                              sx={{
                                color: selectedRole === role.id ? 'primary.main' : 'text.secondary',
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={handleSubmit}
        disabled={!selectedRole || isLoading}
        sx={{ py: 1.5 }}
      >
        {isLoading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          t('auth.continue')
        )}
      </Button>
    </Box>
  );
}
